import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Icon,
  Tabs,
  Tab,
  alpha,
  useTheme,
  Tooltip,
  Alert,
  Divider,
} from "@mui/material";
import moment from "moment";
import { flattenAccounts } from "../../../utils";
import { AccountSelector } from "../../ledger/components/AccountSelector";
import { useConfiguration } from "../../../components/ConfigurationContext";
import { useBranch } from "../../../components/BranchContext";
import {
  IChartOfAccount,
  IListCoaRequest,
} from "../../../accounting.interface";
import { accountingService } from "../../../../../services/accounting";
import { toast } from "react-toastify";

// =====================================================================
// POS MODULE TRANSACTION CODES
// =====================================================================

export const POS_TRANSACTION_CODES = {
  /** Cash sale - finalize with cash payment */
  POS_SALE_CASH: "POS_SALE_CASH",

  /** Card sale - finalize with card payment */
  POS_SALE_CARD: "POS_SALE_CARD",

  /** QR payment sale - finalize with QR payment */
  POS_SALE_QR: "POS_SALE_QR",

  /** Cash surplus detected at shift closure */
  POS_CASH_OVERAGE: "POS_CASH_OVERAGE",

  /** Cash deficit detected at shift closure */
  POS_CASH_SHORTAGE: "POS_CASH_SHORTAGE",

  /** Benefit Sale - finalize with benefit payment */
  POS_SALE_BENEFIT: "POS_SALE_BENEFIT",
} as const;

// =====================================================================
// HR MODULE TRANSACTION CODES
// =====================================================================

export const HR_TRANSACTION_CODES = {
  /** Salary posted but not yet paid */
  HR_SALARY_PAYABLE: "HR_SALARY_PAYABLE",

  /** Salary paid from payable */
  HR_SALARY_SETTLEMENT: "HR_SALARY_SETTLEMENT",

  /** Overtime payment added to payslip */
  HR_OVERTIME: "HR_OVERTIME",

  /** Bonus posted on payslip */
  HR_BONUS: "HR_BONUS",
} as const;

// =====================================================================
// INVENTORY MODULE TRANSACTION CODES
// =====================================================================

export const INVENTORY_TRANSACTION_CODES = {
  /** Inventory purchase - stock item invoice post-GRN */
  PROC_PURCHASE_INVENTORY: "PROC_PURCHASE_INVENTORY",

  /** Non-stock item purchase (e.g., detergent) */
  PROC_PURCHASE_EXPENSE: "PROC_PURCHASE_EXPENSE",

  /** VAT on purchase invoices */
  PROC_PURCHASE_VAT: "PROC_PURCHASE_VAT",

  /** Stock added before invoice - GRN approved */
  INV_GRN_TEMP_STOCK: "INV_GRN_TEMP_STOCK",

  /** Ingredients consumed on POS sale completion */
  INV_RECIPE_CONSUMPTION: "INV_RECIPE_CONSUMPTION",

  /** Warehouse to branch transfer */
  INV_TRANSFER_BRANCH: "INV_TRANSFER_BRANCH",

  /** Physical count lower than system */
  INV_ADJUSTMENT_LOSS: "INV_ADJUSTMENT_LOSS",
} as const;

// =====================================================================
// ACCOUNT MAPPING DEFINITIONS
// =====================================================================

export const TRANSACTION_ACCOUNT_MAPPINGS = {
  // POS Mappings
  POS_SALE_CASH: {
    CASH_ACCOUNT: {
      index: 0,
      label: "Cash/Cash in Hand Account",
      required: true,
    },
    REVENUE_ACCOUNT: {
      index: 1,
      label: "Sales Revenue Account",
      required: true,
    },
    COGS_ACCOUNT: {
      index: 2,
      label: "Cost of Goods Sold Account",
      required: true,
    },
    INVENTORY_ACCOUNT: { index: 3, label: "Inventory Account", required: true },
    TAX_PAYABLE_ACCOUNT: {
      index: 4,
      label: "VAT/Tax Payable Account",
      required: true,
    },
  },
  POS_SALE_CARD: {
    CARD_RECEIVABLE_ACCOUNT: {
      index: 0,
      label: "Card Receivable Account",
      required: true,
    },
    REVENUE_ACCOUNT: {
      index: 1,
      label: "Sales Revenue Account",
      required: true,
    },
    PROCESSING_FEE_EXPENSE_ACCOUNT: {
      index: 2,
      label: "Processing Fee Expense Account",
      required: true,
    },
    COGS_ACCOUNT: {
      index: 3,
      label: "Cost of Goods Sold Account",
      required: true,
    },
    INVENTORY_ACCOUNT: { index: 4, label: "Inventory Account", required: true },
    TAX_PAYABLE_ACCOUNT: {
      index: 5,
      label: "VAT/Tax Payable Account",
      required: true,
    },
  },
  // POS_SALE_QR and POS_SALE_BENEFIT use same as POS_SALE_CARD
  POS_SALE_QR: {
    CARD_RECEIVABLE_ACCOUNT: {
      index: 0,
      label: "QR Receivable Account",
      required: true,
    },
    REVENUE_ACCOUNT: {
      index: 1,
      label: "Sales Revenue Account",
      required: true,
    },
    PROCESSING_FEE_EXPENSE_ACCOUNT: {
      index: 2,
      label: "Processing Fee Expense Account",
      required: true,
    },
    COGS_ACCOUNT: {
      index: 3,
      label: "Cost of Goods Sold Account",
      required: true,
    },
    INVENTORY_ACCOUNT: { index: 4, label: "Inventory Account", required: true },
    TAX_PAYABLE_ACCOUNT: {
      index: 5,
      label: "VAT/Tax Payable Account",
      required: true,
    },
  },
  POS_SALE_BENEFIT: {
    CARD_RECEIVABLE_ACCOUNT: {
      index: 0,
      label: "Benefit Receivable Account",
      required: true,
    },
    REVENUE_ACCOUNT: {
      index: 1,
      label: "Sales Revenue Account",
      required: true,
    },
    PROCESSING_FEE_EXPENSE_ACCOUNT: {
      index: 2,
      label: "Processing Fee Expense Account",
      required: true,
    },
    COGS_ACCOUNT: {
      index: 3,
      label: "Cost of Goods Sold Account",
      required: true,
    },
    INVENTORY_ACCOUNT: { index: 4, label: "Inventory Account", required: true },
    TAX_PAYABLE_ACCOUNT: {
      index: 5,
      label: "VAT/Tax Payable Account",
      required: true,
    },
  },
  POS_CASH_OVERAGE: {
    CASH_ACCOUNT: {
      index: 0,
      label: "Cash/Cash in Hand Account",
      required: true,
    },
    CASH_OVER_ACCOUNT: { index: 1, label: "Cash Over Account", required: true },
  },
  POS_CASH_SHORTAGE: {
    CASH_ACCOUNT: {
      index: 0,
      label: "Cash/Cash in Hand Account",
      required: true,
    },
    CASH_SHORT_ACCOUNT: {
      index: 1,
      label: "Cash Short Account",
      required: true,
    },
  },

  // Inventory Mappings
  PROC_PURCHASE_INVENTORY: {
    INVENTORY_ACCOUNT: { index: 0, label: "Inventory Account", required: true },
    ACCOUNTS_PAYABLE_ACCOUNT: {
      index: 1,
      label: "Accounts Payable Account",
      required: true,
    },
  },
  PROC_PURCHASE_EXPENSE: {
    EXPENSE_ACCOUNT: { index: 0, label: "Expense Account", required: true },
    ACCOUNTS_PAYABLE_ACCOUNT: {
      index: 1,
      label: "Accounts Payable Account",
      required: true,
    },
  },
  PROC_PURCHASE_VAT: {
    VAT_INPUT_ACCOUNT: { index: 0, label: "VAT Input Account", required: true },
    ACCOUNTS_PAYABLE_ACCOUNT: {
      index: 1,
      label: "Accounts Payable Account",
      required: true,
    },
  },
  INV_GRN_TEMP_STOCK: {
    INVENTORY_ACCOUNT: { index: 0, label: "Inventory Account", required: true },
    GRN_CLEARING_ACCOUNT: {
      index: 1,
      label: "GRN Clearing Account",
      required: true,
    },
  },
  INV_RECIPE_CONSUMPTION: {
    COGS_ACCOUNT: {
      index: 0,
      label: "Cost of Goods Sold Account",
      required: true,
    },
    INVENTORY_ACCOUNT: { index: 1, label: "Inventory Account", required: true },
  },
  INV_TRANSFER_BRANCH: {
    TO_BRANCH_INVENTORY_ACCOUNT: {
      index: 0,
      label: "Destination Branch Inventory Account",
      required: true,
    },
    FROM_BRANCH_INVENTORY_ACCOUNT: {
      index: 1,
      label: "Source Branch Inventory Account",
      required: true,
    },
  },
  INV_ADJUSTMENT_LOSS: {
    INVENTORY_ADJUSTMENT_LOSS_ACCOUNT: {
      index: 0,
      label: "Inventory Adjustment Loss Account",
      required: true,
    },
    INVENTORY_ACCOUNT: { index: 1, label: "Inventory Account", required: true },
  },

  // HR Mappings
  HR_SALARY_PAYABLE: {
    SALARY_EXPENSE_ACCOUNT: {
      index: 0,
      label: "Salary Expense Account",
      required: true,
    },
    SALARY_PAYABLE_ACCOUNT: {
      index: 1,
      label: "Salary Payable Account",
      required: true,
    },
    TAX_PAYABLE_ACCOUNT: {
      index: 2,
      label: "Tax Payable Account",
      required: true,
    },
    INSURANCE_PAYABLE_ACCOUNT: {
      index: 3,
      label: "Insurance Payable Account",
      required: false,
    },
  },
  HR_SALARY_SETTLEMENT: {
    SALARY_PAYABLE_ACCOUNT: {
      index: 0,
      label: "Salary Payable Account",
      required: true,
    },
    CASH_BANK_ACCOUNT: { index: 1, label: "Cash/Bank Account", required: true },
    BANK_CHARGES_ACCOUNT: {
      index: 2,
      label: "Bank Charges Account",
      required: false,
    },
  },
  HR_OVERTIME: {
    OVERTIME_EXPENSE_ACCOUNT: {
      index: 0,
      label: "Overtime Expense Account",
      required: true,
    },
    SALARY_PAYABLE_ACCOUNT: {
      index: 1,
      label: "Salary Payable Account",
      required: true,
    },
  },
  HR_BONUS: {
    BONUS_EXPENSE_ACCOUNT: {
      index: 0,
      label: "Bonus Expense Account",
      required: true,
    },
    BONUS_PAYABLE_ACCOUNT: {
      index: 1,
      label: "Bonus Payable Account",
      required: true,
    },
    TAX_PAYABLE_ACCOUNT: {
      index: 2,
      label: "Tax Payable Account",
      required: false,
    },
  },
} as const;

// =====================================================================
// INTERFACE DEFINITIONS
// =====================================================================

interface COAMapping {
  id: string;
  module: "POS" | "HR" | "Inventory";
  transaction_type: string;
  description: string;
  accounts: string[]; // Array of account IDs in order
  is_active: boolean;
  branch_id?: string;
  created_at: number;
  updated_at: number;
}

const moduleConfigs = [
  {
    value: "POS",
    label: "POS Module",
    color: "#10B981",
    icon: "point_of_sale",
    description: "Point of Sale transaction mappings",
    transactionCodes: POS_TRANSACTION_CODES,
  },
  {
    value: "HR",
    label: "HR & Payroll",
    color: "#3B82F6",
    icon: "people",
    description: "Human resources and payroll mappings",
    transactionCodes: HR_TRANSACTION_CODES,
  },
  {
    value: "Inventory",
    label: "Inventory & Procurement",
    color: "#8B5CF6",
    icon: "inventory",
    description: "Inventory and procurement mappings",
    transactionCodes: INVENTORY_TRANSACTION_CODES,
  },
];

// Helper function to create initial mappings for a module
const createInitialMappings = (moduleValue: string): COAMapping[] => {
  const moduleConfig = moduleConfigs.find((m) => m.value === moduleValue);
  if (!moduleConfig) return [];

  return Object.values(moduleConfig.transactionCodes).map(
    (transactionCode) => ({
      id: `${moduleValue.toLowerCase()}_${transactionCode.toLowerCase()}_${Date.now()}`,
      module: moduleValue as "POS" | "HR" | "Inventory",
      transaction_type: transactionCode,
      description: getTransactionTypeDescription(transactionCode),
      accounts: [],
      is_active: false,
      created_at: moment().valueOf(),
      updated_at: moment().valueOf(),
    })
  );
};

// Transaction descriptions
const getTransactionTypeDescription = (transactionType: string) => {
  const descriptions: Record<string, string> = {
    // POS
    POS_SALE_CASH: "When a customer pays with cash at checkout",
    POS_SALE_CARD: "When a customer pays with credit/debit card",
    POS_SALE_QR: "When a customer pays using QR code payment",
    POS_SALE_BENEFIT: "When a customer pays using benefit payment",
    POS_CASH_OVERAGE: "When cash count is higher than expected",
    POS_CASH_SHORTAGE: "When cash count is lower than expected",

    // HR
    HR_SALARY_PAYABLE: "When salary is finalized but not yet paid",
    HR_SALARY_SETTLEMENT: "When clearing salary payable via payment",
    HR_OVERTIME: "When overtime is calculated and added to payroll",
    HR_BONUS: "When bonus is issued to employee",

    // Inventory
    PROC_PURCHASE_INVENTORY: "When inventory items are purchased and invoiced",
    PROC_PURCHASE_EXPENSE: "When non-stock items are purchased",
    PROC_PURCHASE_VAT: "When VAT is applied on purchase invoices",
    INV_GRN_TEMP_STOCK: "When goods are received before invoice",
    INV_RECIPE_CONSUMPTION: "When ingredients are consumed via POS sales",
    INV_TRANSFER_BRANCH: "When inventory is transferred between branches",
    INV_ADJUSTMENT_LOSS: "When physical count is lower than system",
  };

  return descriptions[transactionType] || "Transaction mapping configuration";
};

// Utility function to convert API response to UI format
const convertApiToUiFormat = (apiAccounts: any[]): IChartOfAccount[] => {
  const convertedAccounts: IChartOfAccount[] = apiAccounts.map((account) => ({
    id: account.id,
    accountCode: account.account_code,
    accountName: account.account_name,
    accountType: account.account_type,
    currency: account.currency,
    openingBalance: account.opening_balance,
    currentBalance: account.current_balance,
    parentAccountId: account.parent_account_id || undefined,
    isParent: account.is_parent,
    allowDirectPosting: account.allow_direct_posting,
    level: account.level,
    isActive: account.is_active,
    createdAt: new Date(account.created_at).toISOString(),
    updatedAt: new Date(account.updated_at).toISOString(),
    children: [],
  }));

  return buildAccountHierarchy(convertedAccounts);
};

// Function to build hierarchical structure from flat array
const buildAccountHierarchy = (
  accounts: IChartOfAccount[]
): IChartOfAccount[] => {
  const accountMap = new Map<string, IChartOfAccount>();
  const rootAccounts: IChartOfAccount[] = [];

  // Create map for quick lookup
  accounts.forEach((account) => {
    accountMap.set(account.id, { ...account, children: [] });
  });

  // Build hierarchy
  accounts.forEach((account) => {
    const currentAccount = accountMap.get(account.id);
    if (!currentAccount) return;

    if (account.parentAccountId) {
      const parent = accountMap.get(account.parentAccountId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(currentAccount);
      }
    } else {
      rootAccounts.push(currentAccount);
    }
  });

  return rootAccounts;
};

export const COAMappingsConfig: React.FC = () => {
  const theme = useTheme();
  const { selectedBranch } = useBranch();

  // State management
  const [mappings, setMappings] = useState<COAMapping[]>([]);
  const [selectedModuleTab, setSelectedModuleTab] = useState<number>(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<COAMapping | null>(null);
  const [accounts, setAccounts] = useState<IChartOfAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [formData, setFormData] = useState<{
    accounts: string[];
    description: string;
    is_active: boolean;
  }>({
    accounts: [],
    description: "",
    is_active: false,
  });

  // Initialize mappings for all modules
  useEffect(() => {
    const allMappings: COAMapping[] = [];
    moduleConfigs.forEach((moduleConfig) => {
      allMappings.push(...createInitialMappings(moduleConfig.value));
    });
    setMappings(allMappings);
  }, []);

  // Fetch accounts when branch changes
  useEffect(() => {
    fetchAccounts();
  }, [selectedBranch]);

  const fetchAccounts = async () => {
    if (!selectedBranch || selectedBranch === "organization") {
      setAccounts([]);
      return;
    }

    setAccountsLoading(true);
    try {
      const requestData: IListCoaRequest = {
        pageIndex: 0,
        pageSize: 1000,
        sortField: "account_code",
        sortOrder: "asc",
        selected_branch_id: selectedBranch,
      };

      const response = await accountingService.listCoa(requestData);
      const convertedAccounts = convertApiToUiFormat(response.data);
      setAccounts(convertedAccounts);
    } catch (error: any) {
      console.error("Failed to fetch accounts:", error);
      toast.error("Failed to load accounts. Please try again.");
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const postableAccounts = flattenAccounts(accounts).filter(
    (account: IChartOfAccount) =>
      account.allowDirectPosting && account.isActive !== false
  );

  // Filter mappings by selected module
  const selectedModule = moduleConfigs[selectedModuleTab]?.value;
  const filteredMappings = mappings.filter(
    (mapping) => mapping.module === selectedModule
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedModuleTab(newValue);
  };

  const handleOpenDialog = (mapping: COAMapping) => {
    setEditingMapping(mapping);
    setFormData({
      accounts: [...mapping.accounts],
      description: mapping.description,
      is_active: mapping.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingMapping(null);
    setFormData({
      accounts: [],
      description: "",
      is_active: false,
    });
  };

  const handleSave = () => {
    if (!editingMapping) return;

    const now = moment().valueOf();
    const updatedMappings = mappings.map((mapping) =>
      mapping.id === editingMapping.id
        ? {
            ...mapping,
            ...formData,
            updated_at: now,
            branch_id:
              selectedBranch !== "organization" ? selectedBranch : undefined,
          }
        : mapping
    );
    setMappings(updatedMappings);
    handleCloseDialog();
    toast.success("Account mapping configuration saved successfully");
  };

  const handleToggleActive = (mappingId: string) => {
    const mapping = mappings.find((m) => m.id === mappingId);
    if (!mapping) return;

    // Check if mapping is properly configured before allowing activation
    if (!mapping.is_active && !isMappingConfigured(mapping)) {
      handleOpenDialog(mapping);
      return;
    }

    const updatedMappings = mappings.map((mapping) =>
      mapping.id === mappingId
        ? {
            ...mapping,
            is_active: !mapping.is_active,
            updated_at: moment().valueOf(),
          }
        : mapping
    );
    setMappings(updatedMappings);
    toast.success(
      `Mapping ${
        mappings.find((m) => m.id === mappingId)?.is_active
          ? "disabled"
          : "enabled"
      } successfully`
    );
  };

  const getAccountName = (accountId: string) => {
    const account = postableAccounts.find((acc) => acc.id === accountId);
    return account
      ? `${account.accountCode} - ${account.accountName}`
      : "Not configured";
  };

  const getModuleStats = (moduleValue: string) => {
    const moduleMappings = mappings.filter((m) => m.module === moduleValue);
    const configured = moduleMappings.filter((m) => isMappingConfigured(m));
    const active = moduleMappings.filter(
      (m) => m.is_active && isMappingConfigured(m)
    );

    return {
      total: moduleMappings.length,
      configured: configured.length,
      active: active.length,
      incomplete: moduleMappings.length - configured.length,
    };
  };

  const isMappingConfigured = (mapping: COAMapping) => {
    const accountMappingDef =
      TRANSACTION_ACCOUNT_MAPPINGS[
        mapping.transaction_type as keyof typeof TRANSACTION_ACCOUNT_MAPPINGS
      ];
    if (!accountMappingDef) return false;

    const requiredAccountsCount = Object.values(accountMappingDef).filter(
      (def) => def.required
    ).length;
    const configuredRequiredAccounts = mapping.accounts
      .slice(0, requiredAccountsCount)
      .filter((accountId) => accountId && accountId.trim() !== "");

    return configuredRequiredAccounts.length >= requiredAccountsCount;
  };

  const getMappingStatus = (mapping: COAMapping) => {
    if (!isMappingConfigured(mapping)) {
      return { label: "Not Configured", color: "warning" as const };
    }
    return mapping.is_active
      ? { label: "Active", color: "success" as const }
      : { label: "Inactive", color: "error" as const };
  };

  const handleAccountChange = (index: number, accountId: string) => {
    const newAccounts = [...formData.accounts];
    // Ensure array is large enough
    while (newAccounts.length <= index) {
      newAccounts.push("");
    }
    newAccounts[index] = accountId;
    setFormData((prev) => ({ ...prev, accounts: newAccounts }));
  };

  const getRequiredAccountsStatus = () => {
    if (!editingMapping) return { required: 0, configured: 0 };

    const accountMappingDef =
      TRANSACTION_ACCOUNT_MAPPINGS[
        editingMapping.transaction_type as keyof typeof TRANSACTION_ACCOUNT_MAPPINGS
      ];
    if (!accountMappingDef) return { required: 0, configured: 0 };

    const requiredAccounts = Object.values(accountMappingDef).filter(
      (def) => def.required
    );
    const configuredRequired = requiredAccounts.filter(
      (_, index) =>
        formData.accounts[index] && formData.accounts[index].trim() !== ""
    );

    return {
      required: requiredAccounts.length,
      configured: configuredRequired.length,
    };
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 0.5,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
          >
            Chart of Accounts Mappings
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", fontSize: "1rem" }}
          >
            Configure account mappings for automated journal entries across
            modules
          </Typography>
        </Box>
        {selectedBranch === "organization" && (
          <Alert severity="warning" sx={{ maxWidth: 300 }}>
            Please select a specific branch to configure account mappings
          </Alert>
        )}
      </Box>

      <Box sx={{ flex: 1, p: 3 }}>
        {/* Module Tabs */}
        <Box
          sx={{
            mb: 3,
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Tabs
            value={selectedModuleTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 64,
                px: 3,
                "&.Mui-selected": {
                  color: "primary.main",
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {moduleConfigs.map((moduleConfig, index) => (
              <Tab
                key={moduleConfig.value}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: moduleConfig.color,
                      }}
                    />
                    <Icon sx={{ fontSize: 18 }}>{moduleConfig.icon}</Icon>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {moduleConfig.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        {getModuleStats(moduleConfig.value).active}/
                        {getModuleStats(moduleConfig.value).total} active
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Summary Cards for Selected Module */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "primary.main" }}
                >
                  {getModuleStats(selectedModule).total}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Total Mappings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "success.main" }}
                >
                  {getModuleStats(selectedModule).active}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Active & Configured
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "info.main" }}
                >
                  {getModuleStats(selectedModule).configured}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Configured
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "warning.main" }}
                >
                  {getModuleStats(selectedModule).incomplete}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Need Configuration
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Module Description */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Icon
                sx={{
                  fontSize: 24,
                  color: moduleConfigs[selectedModuleTab]?.color,
                }}
              >
                {moduleConfigs[selectedModuleTab]?.icon}
              </Icon>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {moduleConfigs[selectedModuleTab]?.label} Mappings
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {moduleConfigs[selectedModuleTab]?.description}. Configure the
              required accounts for each transaction code, then enable the
              mappings you want to use.
            </Typography>
          </CardContent>
        </Card>

        {/* Mappings Table */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>
                      Transaction Code
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Account Configuration
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMappings.map((mapping) => {
                    const status = getMappingStatus(mapping);
                    const isConfigured = isMappingConfigured(mapping);
                    const accountMappingDef =
                      TRANSACTION_ACCOUNT_MAPPINGS[
                        mapping.transaction_type as keyof typeof TRANSACTION_ACCOUNT_MAPPINGS
                      ];

                    return (
                      <TableRow key={mapping.id}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontFamily: "monospace" }}
                          >
                            {mapping.transaction_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {mapping.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {accountMappingDef ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              {Object.entries(accountMappingDef)
                                .slice(0, 3)
                                .map(([key, def], index) => (
                                  <Typography
                                    key={key}
                                    variant="caption"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.7rem",
                                      color: mapping.accounts[index]
                                        ? "text.primary"
                                        : def.required
                                        ? "error.main"
                                        : "text.secondary",
                                      fontStyle: mapping.accounts[index]
                                        ? "normal"
                                        : "italic",
                                    }}
                                  >
                                    {def.label}:{" "}
                                    {mapping.accounts[index]
                                      ? getAccountName(mapping.accounts[index])
                                      : def.required
                                      ? "Required"
                                      : "Optional"}
                                  </Typography>
                                ))}
                              {Object.keys(accountMappingDef).length > 3 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    fontStyle: "italic",
                                  }}
                                >
                                  +{Object.keys(accountMappingDef).length - 3}{" "}
                                  more accounts...
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontStyle: "italic",
                              }}
                            >
                              No configuration available
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={status.label}
                            color={status.color}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Configure mapping">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(mapping)}
                                sx={{ color: "primary.main" }}
                                disabled={selectedBranch === "organization"}
                              >
                                <Icon>settings</Icon>
                              </IconButton>
                            </Tooltip>
                            {/* <Tooltip
                              title={
                                selectedBranch === "organization"
                                  ? "Select a branch first"
                                  : !isConfigured
                                  ? "Configure accounts first"
                                  : mapping.is_active
                                  ? "Disable mapping"
                                  : "Enable mapping"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleActive(mapping.id)}
                                  disabled={
                                    selectedBranch === "organization" ||
                                    (!isConfigured && !mapping.is_active)
                                  }
                                  sx={{
                                    color:
                                      mapping.is_active && isConfigured
                                        ? "warning.main"
                                        : "success.main",
                                  }}
                                >
                                  <Icon>
                                    {mapping.is_active && isConfigured
                                      ? "pause"
                                      : "play_arrow"}
                                  </Icon>
                                </IconButton>
                              </span>
                            </Tooltip> */}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Enhanced Configuration Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: "70vh",
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ color: "primary.main" }}>account_tree</Icon>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Configure Account Mapping
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {editingMapping?.transaction_type}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Transaction Info */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Transaction Scenario:
            </Typography>
            <Typography variant="body2">
              {editingMapping
                ? getTransactionTypeDescription(editingMapping.transaction_type)
                : ""}
            </Typography>
          </Alert>

          {/* Progress Indicator */}
          {editingMapping &&
            (() => {
              const { required, configured } = getRequiredAccountsStatus();
              return (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Configuration Progress
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {configured}/{required} required accounts configured
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${
                          required > 0 ? (configured / required) * 100 : 0
                        }%`,
                        height: "100%",
                        bgcolor:
                          configured === required
                            ? "success.main"
                            : "warning.main",
                        transition: "all 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>
              );
            })()}

          {/* Account Selectors */}
          {editingMapping &&
            (() => {
              const accountMappingDef =
                TRANSACTION_ACCOUNT_MAPPINGS[
                  editingMapping.transaction_type as keyof typeof TRANSACTION_ACCOUNT_MAPPINGS
                ];
              if (!accountMappingDef) return null;

              return (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Account Configuration
                  </Typography>

                  <Grid container spacing={3}>
                    {Object.entries(accountMappingDef).map(([key, def]) => (
                      <Grid item xs={12} md={6} key={key}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            border: `2px solid ${
                              formData.accounts[def.index]
                                ? alpha(theme.palette.success.main, 0.3)
                                : def.required
                                ? alpha(theme.palette.error.main, 0.3)
                                : alpha(theme.palette.divider, 0.2)
                            }`,
                            bgcolor: formData.accounts[def.index]
                              ? alpha(theme.palette.success.main, 0.05)
                              : def.required && !formData.accounts[def.index]
                              ? alpha(theme.palette.error.main, 0.05)
                              : alpha(theme.palette.background.default, 0.5),
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: def.required
                                  ? "error.main"
                                  : "info.main",
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: def.required
                                  ? "error.main"
                                  : "info.main",
                                fontWeight: 600,
                                textTransform: "uppercase",
                              }}
                            >
                              {def.required ? "Required" : "Optional"}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              #{def.index + 1}
                            </Typography>
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            {def.label}
                          </Typography>

                          <AccountSelector
                            selectedAccountId={
                              formData.accounts[def.index] || ""
                            }
                            onAccountChange={(accountId) =>
                              handleAccountChange(def.index, accountId)
                            }
                            postingAccounts={postableAccounts}
                            selectedAccount={
                              postableAccounts.find(
                                (acc) => acc.id === formData.accounts[def.index]
                              ) || null
                            }
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })()}

          <Divider sx={{ my: 3 }} />

          {/* Additional Settings */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Additional Settings
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                    disabled={(() => {
                      const { required, configured } =
                        getRequiredAccountsStatus();
                      return configured < required;
                    })()}
                  />
                }
                label="Enable this mapping"
                sx={{ mt: 2 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                multiline
                rows={2}
                placeholder="Describe when this mapping should be used"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDialog} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={(() => {
              const { required, configured } = getRequiredAccountsStatus();
              return configured < required;
            })()}
            size="large"
            startIcon={<Icon>save</Icon>}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
